import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, DEFAULT_LOCALE } from '@/hooks/useLanguage';
import { useStorefront, STOREFRONT_POST_LOCALE, ROOT_PRODUCT_STOREFRONTS } from '@/hooks/useStorefront';
import NotFound from './NotFound';

// Lazy: schwere Pages (framer-motion, date-fns, dompurify) raus aus Initial-Bundle
const CategoryPage = lazy(() => import('./CategoryPage'));
const BlogPostPage = lazy(() => import('./BlogPostPage'));
const BlogCategoryPage = lazy(() => import('./BlogCategoryPage'));
const ProductPage = lazy(() => import('./ProductPage'));

type SlugType = 'product-category' | 'product' | 'blog-category' | 'blog-post' | 'not-found';

/**
 * Storefronts whose products live on the URL ROOT (/instagram-follower)
 * instead of under the /produkt/ prefix. AL migrates 1:1 from its legacy
 * WooCommerce URLs. On CH/AT/IT this list is empty behaviour-wise, so
 * /instagram-likes keeps 404-ing there exactly like today.
 */
// Root-product storefronts live in useStorefront.ts (single source of truth).

export default function SlugResolver() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { locale } = useLanguage();
  const { storefront } = useStorefront();
  const [resolved, setResolved] = useState<SlugType | null>(null);

  useEffect(() => {
    if (!slug) {
      setResolved('not-found');
      return;
    }

    let active = true;

    const resolve = async () => {
      setResolved(null);

      const postLocale = STOREFRONT_POST_LOCALE[storefront] ?? 'de';
      const rootProducts = (ROOT_PRODUCT_STOREFRONTS as readonly string[]).includes(storefront);

      const [redirectRes, catRes, blogCatRes, postRes, catTransRes, postTransRes, productRes] = await Promise.all([
        supabase
          .from('redirects')
          .select('target_path, status_code, storefront')
          .eq('source_path', `/${slug}`)
          .eq('is_active', true)
          .or(`storefront.is.null,storefront.eq.${storefront}`)
          .order('storefront', { ascending: false, nullsFirst: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('categories')
          .select('id')
          .eq('slug', slug)
          .maybeSingle(),
        supabase
          .from('blog_categories')
          .select('id')
          .eq('slug', slug)
          .maybeSingle(),
        supabase
          .from('blog_posts')
          .select('id, locale')
          .eq('slug', slug)
          .eq('locale', postLocale)
          .maybeSingle(),
        supabase
          .from('category_translations')
          .select('category_id')
          .eq('slug', slug)
          .limit(1)
          .maybeSingle(),
        supabase
          .from('blog_post_translations')
          .select('post_id')
          .eq('slug', slug)
          .limit(1)
          .maybeSingle(),
        // 7th query — ROOT product lookup, only on root-product storefronts.
        rootProducts
          ? supabase
              .from('product_translations')
              .select('product_id')
              .eq('locale', postLocale)
              .eq('slug', slug)
              .limit(1)
              .maybeSingle()
          : Promise.resolve({ data: null } as { data: null }),
      ]);

      if (!active) return;

      // Priority: redirect > category > product > blog category > blog post > not found
      if (redirectRes.data) {
        navigate(redirectRes.data.target_path, { replace: true });
        return;
      }
      if (catRes.data) { setResolved('product-category'); return; }
      if (catTransRes?.data) { setResolved('product-category'); return; }
      if (productRes?.data) { setResolved('product'); return; }
      if (blogCatRes.data) { setResolved('blog-category'); return; }
      if (postRes.data) { setResolved('blog-post'); return; }
      if (postTransRes?.data) { setResolved('blog-post'); return; }

      setResolved('not-found');
    };

    resolve();

    return () => { active = false; };
  }, [slug, navigate, locale, storefront]);

  if (resolved === null) {
    return <div className="min-h-screen" />;
  }

  const fallback = <div className="min-h-screen" />;
  switch (resolved) {
    case 'product-category':
      return <Suspense fallback={fallback}><CategoryPage /></Suspense>;
    case 'product':
      return <Suspense fallback={fallback}><ProductPage /></Suspense>;
    case 'blog-category':
      return <Suspense fallback={fallback}><BlogCategoryPage /></Suspense>;
    case 'blog-post':
      return <Suspense fallback={fallback}><BlogPostPage /></Suspense>;
    default:
      return <NotFound />;
  }
}
